<template>
  <AllocationCardLayout
    icon="shopping-cart"
    :color="PALETTE.semanticOverrides.portfolioType['Spot Sales']"
    :title="$t('production_strategy.spot_sale')"
    :amount="allocation.amount"
    :unit="$t('common.mwh')"
    :subtitle="$t('production_strategy.allocation')"
    :ariaLabel="$t('production_strategy.spot_allocation_card')"
  >
    <template #badge>
      <div class="d-flex flex-wrap gap-2">
        <span
          v-for="group in strategyGroups"
          :key="`${group.strategy}-${group.minimumPrice || 'market'}`"
          class="badge text-white px-3 py-2"
          :style="{
            backgroundColor:
              PALETTE.semanticOverrides.portfolioType['Spot Sales'],
          }"
        >
          <template v-if="group.strategy === 'minimum'">
            Min €{{ group.minimumPrice }}/{{ $t("common.mwh") }}
          </template>
          <template v-else>
            {{
              $t("tradingCenter.overview.spot.visualization.highestBid.title")
            }}
          </template>
          <template v-if="!isSingleCompany && strategyGroups.length > 1">
            <small class="ms-2 opacity-75">
              ({{ group.companies.map((c) => c.name).join(", ") }})
            </small>
          </template>
        </span>
        <!-- Duration badge for minimum strategy -->
        <span
          v-if="isSingleCompany && activeMinPrice > 0"
          class="badge bg-secondary text-white px-3 py-2"
        >
          {{
            $t("setPriceTerms.duration.limit_active_for", {
              duration: currentDuration,
            })
          }}
        </span>
      </div>
    </template>
    <template #action>
      <EditButton v-if="canEdit" @click="openSpotSettings" />
    </template>
  </AllocationCardLayout>

  <!-- Spot Strategy Settings Modal -->
  <ModalComponent
    ref="spotModalRef"
    :submit-button-text="$t('common.save')"
    :submit="saveSpotSettings"
    size="lg"
  >
    <template #title>{{ $t("production_strategy.spot_settings") }}</template>
    <template #body>
      <SpotStrategyPicker
        :initialStrategy="initialSpotStrategy"
        @update:strategy="handleSpotStrategyChange"
      />
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import EditButton from "@/components/common/EditButton.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { PALETTE } from "@/composables/chartPalette";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { getSpotMinimumPrice } from "@/composables/useSpotMinimumPrice";
import type { Company } from "@/types";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import SpotStrategyPicker from "@/dashboard/common/SpotStrategyPicker.vue";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import AllocationCardLayout from "../AllocationCardLayout.vue";
import { useCompanyMode } from "../useCompanyMode";

const props = defineProps<{
  allocation: {
    amount: number;
    percentage: number;
  };
  companies: Company[];
}>();

const emit = defineEmits<{
  success: [message: string];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const productionStore = useProductionStrategyStore();

// Multi-company mode detection
const { isSingleCompany, currentCompany, canEdit } = useCompanyMode(
  computed(() => props.companies)
);

const spotModalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("spotModalRef");
const currentSpotStrategy = ref<{
  type: "market" | "minimum";
  minimumPrice?: number;
  durationMonths?: number;
}>({ type: "market", minimumPrice: undefined, durationMonths: undefined });

// Get spot strategies for all companies
const companySpotStrategies = computed(() => {
  if (isSingleCompany.value) {
    const minPrice = getSpotMinimumPrice(productionStore.portfolios);
    return [
      {
        company: currentCompany.value,
        strategy: minPrice > 0 ? "minimum" : "market",
        minimumPrice: minPrice > 0 ? minPrice : undefined,
      },
    ];
  }

  // Multi-company mode - get strategy for each company
  return props.companies.map((company) => {
    const companyPortfolios = productionStore.portfolios.filter(
      (p) => p.company_id === company.id
    );
    const minPrice = getSpotMinimumPrice(companyPortfolios);
    return {
      company,
      strategy: minPrice > 0 ? "minimum" : "market",
      minimumPrice: minPrice > 0 ? minPrice : undefined,
    };
  });
});

// Group strategies by type for display
const strategyGroups = computed(() => {
  const groups = new Map<
    string,
    { strategy: string; companies: Company[]; minimumPrice?: number }
  >();

  companySpotStrategies.value.forEach(({ company, strategy, minimumPrice }) => {
    if (!company) return;

    const key = strategy === "minimum" ? `minimum-${minimumPrice}` : "market";

    if (!groups.has(key)) {
      groups.set(key, {
        strategy,
        companies: [],
        minimumPrice,
      });
    }

    const group = groups.get(key);
    if (group) {
      group.companies.push(company);
    }
  });

  return Array.from(groups.values());
});

// Computed strategy for the modal using shared typed logic (single company only)
const activeMinPrice = computed<number>(() => {
  if (isSingleCompany.value) {
    return getSpotMinimumPrice(productionStore.portfolios);
  }
  return 0;
});

const initialSpotStrategy = computed<{
  type: "market" | "minimum";
  minimumPrice?: number;
  durationMonths?: number;
}>(() => {
  const minPrice = activeMinPrice.value;
  const durationMonths = currentCompany.value?.id
    ? productionStore.getDurationMonthsForCompany(currentCompany.value.id)
    : 6;

  return {
    type: minPrice > 0 ? "minimum" : "market",
    minimumPrice: minPrice || undefined,
    durationMonths: minPrice > 0 ? durationMonths : undefined,
  };
});

// Get current duration for badge display
const currentDuration = computed(() => {
  return currentCompany.value?.id
    ? productionStore.getDurationMonthsForCompany(currentCompany.value.id)
    : 6;
});

// Modal handlers
const openSpotSettings = () => {
  if (spotModalRef.value) {
    spotModalRef.value.showModal = true;
  }
};

const handleSpotStrategyChange = (strategy: {
  type: "market" | "minimum";
  minimumPrice?: number;
  durationMonths?: number;
  isValid: boolean;
}) => {
  if (strategy.isValid) {
    currentSpotStrategy.value = {
      type: strategy.type,
      minimumPrice: strategy.minimumPrice,
      durationMonths: strategy.durationMonths,
    };
  }
};

const saveSpotSettings = async () => {
  try {
    if (!currentCompany.value?.id) {
      throw new Error("Missing company id");
    }
    const success = await productionStore.saveSpotStrategy(
      currentCompany.value.id,
      {
        type: currentSpotStrategy.value.type,
        minimumPrice: currentSpotStrategy.value.minimumPrice,
        durationMonths: currentSpotStrategy.value.durationMonths,
      }
    );

    if (success) {
      globalSuccessHandler(
        t("dashboard.companyData.update_min_price_success"),
        t("dashboard.companyData.new_min_price_effective_immediately")
      );

      // Close the modal
      if (spotModalRef.value) {
        spotModalRef.value.showModal = false;
      }

      // Emit success event to parent for data refresh
      emit("success", "Spot strategy updated successfully");
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};
</script>

<style scoped lang="scss"></style>
